import Form, {
  ButtonItem,
  ButtonOptions,
  EmptyItem,
  GroupItem,
  Label,
  SimpleItem,
} from 'devextreme-react/form';
import { RequiredRule } from 'devextreme-react/validator';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import useSWR from 'swr';
import { postApi, putApi } from '../../apiUtils';
import { Loader } from '../../components/loader/Loader';
import { useAssetClass } from '../../contexts/assetClass';
import { useLoading } from '../../contexts/loadingContext';
import { useTranslation } from '../../contexts/translation';
import useUnsavedChangesWarning from '../../hooks/useUnsavedChangesWarning';
import { swrConfig } from '../../pages/Assets/AssetsEdit';
import { decimalToTimespan, timeSpanToHoursMinutes } from '../../pages/Tasks/Columns';
import { Asset } from '../../types/asset.types';
import { Customer } from '../../types/customer.types';
import {
  CustomerProjectBillingStatus,
  CustomerProjectBillingStatusTranslation,
  CustomerProjectTypeStatus,
  CustomerProjectTypeTranslation,
} from '../../types/customerProject.types';
import { IForm } from '../../types/iForm';
import { Person } from '../../types/person.types';
import { Task } from '../../types/task.types';
import {
  getColorClass,
  getUserColorClass,
  UserColor,
  UserColors,
} from '../../types/userColor.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { dateFieldOptionsWithSerialization } from '../../utils/dates.types';
import { formTranslationByEdit } from '../../utils/formTranslationByEdit';
import { dxSelectBoxConfig } from '../tasks/TasksForm';

export interface ICustomersProjectsForm extends IForm {
  responsibleInternalUserId?: number;
  totalElapsedTime?: string;
  projectId?: number;
  onSuccess?: () => void;
}

export const CustomersProjectsForm = ({
  projectId,
  formData,
  isEdit,
  responsibleInternalUserId,
  totalElapsedTime,
  onSuccess,
}: ICustomersProjectsForm) => {
  const { assetClass } = useAssetClass();
  const { translate, lang } = useTranslation();
  const transFormName = formTranslationByEdit('CustomersProjects', isEdit);
  const { loading } = useLoading();
  const history = useHistory();
  const backRoute = `/customers-projects`;
  const formRef = useRef<Form>(null);
  const [prompt, setDirty, setClean, handleCloseButton] = useUnsavedChangesWarning(
    '',
    (backRouteObject) => {
      if (!formRef) return;
      if (formRef.current!.instance.validate().isValid) {
        setClean();
        onSubmit(null).then((result) => {
          if (result) {
            if (!backRouteObject) history.goBack();
            else history.push(backRouteObject);
          } else setDirty();
        });
      }
    },
  );

  // @ts-ignore
  const [showTasksDiagnoses, setShowTasksDiagnoses] = useState(
    // @ts-ignore
    formData && formData.type === CustomerProjectTypeStatus.ONE_OFF ? true : false,
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    // @ts-ignore
    formData ? formData.customerId : null,
  );
  const [selectedTopLevelAssetId, setSelectedTopLevelAssetId] = useState<number | null>(
    // @ts-ignore
    formData ? formData.assetTopLevelParenId : null,
  );
  const [userColorClass, setUserColorClass] = useState<string | null>(null);
  const [projectColorClass, setProjectColorClass] = useState<string | null>(
    // @ts-ignore
    formData ? getColorClass(formData.color) : null,
  );
  const [projectStatusColorClass, setProjectStatusColorClass] = useState<string | null>(null);

  const { data: customers } = useSWR<Customer[]>(`${API_URL.CUSTOMER_LIST}`, fetcher, swrConfig);
  const { data: projectStatuses } = useSWR<Customer[]>(
    `${API_URL.CUSTOMER_PROJECT_STATUS_LIST}`,
    fetcher,
    swrConfig,
  );
  const { data: responsiblePersons } = useSWR<Person[]>(API_URL.PERSON_FROM_THIS_APP_LIST, fetcher);
  const { data: userColors } = useSWR<UserColor[]>(
    `${API_URL.USER_COLOR_LIST}`,
    fetcher,
    swrConfig,
  );
  const { data: topLevelAssets } = useSWR<Asset[]>(
    `${API_URL.ASSET_LIST_BY_CLASS_TOP_LEVEL}?classId=${assetClass}&activeOnly=true&visibleOnly=true`,
    fetcher,
    swrConfig,
  );
  const { data: assetsAssignToCustomer } = useSWR<Asset[]>(
    selectedCustomerId
      ? `${API_URL.ASSET_LIST_FOR_CUSTOMER}?customerId=${selectedCustomerId}`
      : null,
    fetcher,
    swrConfig,
  );
  const { data: assets } = useSWR<Asset[]>(
    selectedTopLevelAssetId
      ? `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}&parentId=${selectedTopLevelAssetId}&assetToInclude=${selectedTopLevelAssetId}`
      : `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}`,
    fetcher,
    swrConfig,
  );
  const { data: tasks } = useSWR<Task[]>(
    projectId
      ? `${API_URL.TASK_BY_CUSTOMER_PROJECT}?projectId=${projectId}&includeTaskDescription=false&includeTaskDiagnose=true`
      : null,
    fetcher,
    swrConfig,
  );

  const handleResponsiblePersonChanged = (userColors: UserColor[], userId: number) => {
    const colorClass = getUserColorClass(userColors, userId);
    setUserColorClass(colorClass);
  };

  useEffect(() => {
    if (!userColors || !responsibleInternalUserId) return;
    handleResponsiblePersonChanged(userColors, responsibleInternalUserId);
  }, [userColors]);

  useEffect(() => {
    if (!projectStatuses || !formData) return;
    // @ts-ignore
    const color = projectStatuses.find((x) => x.id === formData.projectStatusId);
    // @ts-ignore
    if (color) setProjectStatusColorClass(getColorClass(color.color));
  }, [projectStatuses]);

  const onSubmit = async (e) => {
    if (e !== null) e.preventDefault();
    if (!formData) return;
    let isOk = false;

    if (isEdit)
      await putApi({
        url: API_URL.CUSTOMER_PROJECT_PUT,
        data: {
          ...formData,
          plannedTimeBudget:
            // @ts-ignore
            formData && formData.plannedTimeBudget
              ? // @ts-ignore
                decimalToTimespan(Number(formData.plannedTimeBudget.replace(',', '.')))
              : null,
        },
        callAfterSuccess: () => {
          isOk = true;
          if (onSuccess) onSuccess();
        },
      });
    else
      await postApi({
        url: API_URL.CUSTOMER_PROJECT_POST,
        data: {
          ...formData,
          plannedTimeBudget:
            // @ts-ignore
            formData && formData.plannedTimeBudget
              ? // @ts-ignore
                decimalToTimespan(Number(formData.plannedTimeBudget.replace(',', '.')))
              : null,
        },
        callAfterSuccess: () => {
          isOk = true;
        },
      });

    return isOk;
  };

  const onHtmlEditorContentReady = useCallback((e: any) => {
    const container = e.element.closest('.dx-htmleditor');
    if (container) {
      container.style.resize = 'vertical';
      container.style.overflow = 'auto';
      container.style.minHeight = '150px';
    }
  }, []);

  const htmlEditorOptions = useMemo(
    () => ({
      height: 100,
      resizable: { handles: 's' },
      toolbar: {
        items: [
          'undo',
          'redo',
          'bold',
          'italic',
          'underline',
          'strike',
          'alignLeft',
          'alignCenter',
          'alignRight',
          'alignJustify',
          'orderedList',
          'bulletList',
          'fontSize',
          'fontColor',
          'color',
          'background',
          'link',
          'clear',
        ],
      },
      mediaResizing: { enabled: true },
      onContentReady: onHtmlEditorContentReady,
    }),
    [onHtmlEditorContentReady],
  );

  useEffect(() => {
    if (!tasks || !formRef || !formRef.current || !formRef.current.instance) return;

    let content = '';
    tasks
      .filter((x) => x.diagnose && x.diagnose.trim() !== '')
      .forEach((item) => {
        content += `--- ${item.customId} ${item.taskName} ---<br />${
          item.diagnose || ``
        }<br /><br />`;
      });

    setTimeout(() => {
      if (formRef && formRef.current && formRef.current.instance)
        formRef.current.instance.updateData('tasksDiagnoses', content);
    }, 1000);
  }, [tasks, formRef, formRef.current]);

  if (!projectStatuses || !responsiblePersons || !userColors || !customers || !assets)
    return <Loader />;
  return (
    <form
      onSubmit={(e) => {
        onSubmit(e);
        if (formRef && formRef.current && !isEdit) formRef.current?.instance.resetValues();
        // @ts-ignore
        setClean();
      }}
    >
      <Form
        colCount={3}
        formData={formData}
        showValidationSummary={true}
        onFieldDataChanged={(e) => {
          if (e.dataField === 'tasksDiagnoses') return;
          // @ts-ignore
          setDirty();
        }}
        ref={formRef}
      >
        <SimpleItem
          dataField={'customerId'}
          editorType={'dxSelectBox'}
          editorOptions={{
            ...dxSelectBoxConfig,
            valueExpr: 'id',
            displayExpr: 'name',
            dataSource: customers,
            onValueChanged: (e: any) => setSelectedCustomerId(e.value),
          }}
        >
          <Label text={translate!('Customer', transFormName)} />
          <RequiredRule message={translate!('Select customer', transFormName)} />
        </SimpleItem>
        <SimpleItem dataField="name" editorType="dxTextBox">
          <Label text={translate!('Name', transFormName)} />
          <RequiredRule message={translate!('Enter name', transFormName)} />
        </SimpleItem>
        <SimpleItem
          dataField={'type'}
          editorType={'dxSelectBox'}
          editorOptions={{
            ...dxSelectBoxConfig,
            valueExpr: 'id',
            displayExpr: 'name',
            dataSource: Object.values(CustomerProjectTypeStatus).map(
              (status: CustomerProjectTypeStatus) => {
                return {
                  id: status,
                  name: translate!(
                    CustomerProjectTypeTranslation[status],
                    `CustomersProjects.Type`,
                  ),
                };
              },
            ),
            onValueChanged: (e: any) => {
              setShowTasksDiagnoses(e.value === CustomerProjectTypeStatus.ONE_OFF);
            },
          }}
        >
          <Label text={translate!('Type', transFormName)} />
          <RequiredRule message={translate!('Select type', transFormName)} />
        </SimpleItem>
        <SimpleItem
          cssClass={`dropdown-user-color ${projectStatusColorClass}`}
          dataField={'projectStatusId'}
          editorType={'dxSelectBox'}
          editorOptions={{
            ...dxSelectBoxConfig,
            valueExpr: 'id',
            displayExpr: 'name',
            dataSource: projectStatuses,
            onValueChanged: (e) => {
              const color = projectStatuses.find((x) => x.id === e.value);
              // @ts-ignore
              if (color) setProjectStatusColorClass(getColorClass(color.color));
            },
            itemTemplate: (data: any, _index: number, element: any) => {
              const color = projectStatuses.find((x) => x.id === data.id);
              if (color)
                element.innerHTML = `<div style="color: #${color.color}">${data.name}</div>`;
              else element.innerHTML = `<div>${data.name}</div>`;
            },
          }}
        >
          <Label text={translate!('Project status', transFormName)} />
          <RequiredRule message={translate!('Select project status', transFormName)} />
        </SimpleItem>
        <SimpleItem
          dataField={'defaultBillingStatus'}
          editorType={'dxSelectBox'}
          editorOptions={{
            ...dxSelectBoxConfig,
            valueExpr: 'id',
            displayExpr: 'name',
            dataSource: Object.values(CustomerProjectBillingStatus).map(
              (status: CustomerProjectBillingStatus) => {
                return {
                  id: status,
                  name: translate!(
                    CustomerProjectBillingStatusTranslation[status],
                    `CustomersProjects.Status`,
                  ),
                };
              },
            ),
          }}
        >
          <Label text={translate!('Billing status', transFormName)} />
        </SimpleItem>
        <SimpleItem
          dataField="goLiveDate"
          editorType="dxDateBox"
          editorOptions={{
            ...dateFieldOptionsWithSerialization,
          }}
        >
          <Label text={translate!('Go live date', transFormName)} />
        </SimpleItem>
        <SimpleItem
          dataField="plannedTimeBudget"
          editorType="dxTextBox"
          editorOptions={{
            onValueChanged: (e) => {
              if (e.value === undefined) return;

              if (Number.isNaN(Number(e.value.replace(',', '.')))) {
                e.component.option('value', undefined);
                return;
              }
            },
          }}
        >
          <Label text={translate!('Planned time budget', transFormName)} />
        </SimpleItem>

        <SimpleItem
          cssClass={`dropdown-user-color ${userColorClass}`}
          dataField="responsibleInternalUserId"
          editorType="dxSelectBox"
          editorOptions={{
            ...dxSelectBoxConfig,
            valueExpr: 'personalNumber',
            displayExpr: 'fullName',
            items: responsiblePersons,
            onValueChanged: (e) => handleResponsiblePersonChanged(userColors, e.value),
            itemTemplate: (data: any, _index: number, element: any) => {
              const color = userColors.find((x) => x.userId === data.personalNumber);
              if (color)
                element.innerHTML = `<div style="color: #${color.color}">${data.fullName}</div>`;
              else element.innerHTML = `<div>${data.fullName}</div>`;
            },
          }}
        >
          <Label text={translate!('Responsible person', transFormName)} />
        </SimpleItem>
        <SimpleItem dataField="responsibleForCustomer" editorType="dxTextBox">
          <Label text={translate!('Responsible for customer', transFormName)} />
        </SimpleItem>

        {totalElapsedTime ? (
          <GroupItem>
            <span style={{ color: '#8899a8' }}>Celkem vykázáno: </span>
            <span style={{ color: '#8899a8', fontWeight: 'bold' }}>
              {timeSpanToHoursMinutes(totalElapsedTime)}
            </span>
          </GroupItem>
        ) : (
          isEdit && <GroupItem />
        )}

        <GroupItem>
          <SimpleItem
            cssClass={`dropdown-user-color ${projectColorClass ? projectColorClass : ''}`}
            dataField="color"
            editorType="dxSelectBox"
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'value',
              displayExpr: 'name',
              items: UserColors.map((color) => {
                return {
                  ...color,
                  name: translate!(color.name, 'UserColor.Color'),
                };
              }),
              onValueChanged: (e) => setProjectColorClass(getColorClass(e.value)),
              itemTemplate: (data: any, _index: number, element: any) => {
                element.innerHTML = `<div style="color: #${data.value}">${data.name}</div>`;
              },
            }}
          >
            <Label text={translate!('Color', transFormName)} />
          </SimpleItem>
        </GroupItem>

        {!isEdit && <EmptyItem />}
        <GroupItem>
          <SimpleItem dataField="visible" editorType="dxCheckBox">
            <Label text={translate!('Visible', transFormName)} />
          </SimpleItem>
        </GroupItem>

        <SimpleItem
          dataField={'assetTopLevelParenId'}
          editorType={'dxSelectBox'}
          editorOptions={{
            ...dxSelectBoxConfig,
            valueExpr: 'id',
            displayExpr: 'name',
            dataSource: selectedCustomerId
              ? topLevelAssets?.filter((asset) =>
                  assetsAssignToCustomer?.some((assigned) => assigned.id === asset.id),
                )
              : topLevelAssets,
            onValueChanged: (e: any) => setSelectedTopLevelAssetId(e.value),
          }}
        >
          <Label text={translate!('Parent asset', transFormName)} />
        </SimpleItem>
        <SimpleItem
          dataField={'assetId'}
          editorType={'dxSelectBox'}
          editorOptions={{
            ...dxSelectBoxConfig,
            valueExpr: 'id',
            displayExpr: 'name',
            dataSource:
              selectedCustomerId && assets
                ? assets.filter((asset) =>
                    assetsAssignToCustomer?.some((assigned) => assigned.id === asset.id),
                  )
                : assets,
          }}
        >
          <Label text={translate!('Asset', transFormName)} />
        </SimpleItem>
        <EmptyItem />

        <SimpleItem
          dataField="assignmentDescription"
          editorType="dxHtmlEditor"
          colSpan={3}
          editorOptions={htmlEditorOptions}
        >
          <Label location="top" text={translate!('Assignment description', transFormName)} />
        </SimpleItem>

        <SimpleItem
          dataField="solutionDescription"
          editorType="dxHtmlEditor"
          colSpan={3}
          editorOptions={htmlEditorOptions}
        >
          <Label location={'top'} text={translate!('Solution description', transFormName)} />
        </SimpleItem>

        {showTasksDiagnoses && isEdit && (
          <SimpleItem
            colSpan={3}
            dataField="tasksDiagnoses"
            editorType="dxHtmlEditor"
            editorOptions={{
              height: 150,
              readOnly: true,
            }}
          >
            <Label location={'top'} text={translate!('Tasks solutions', transFormName)} />
          </SimpleItem>
        )}

        <SimpleItem
          dataField="notes"
          editorType="dxHtmlEditor"
          colSpan={3}
          editorOptions={htmlEditorOptions}
        >
          <Label location={'top'} text={translate!('Notes', transFormName)} />
        </SimpleItem>

        <GroupItem colCount={2}>
          <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
            <ButtonOptions
              width={'100%'}
              text={translate!('Close', 'Asset.Update')}
              type={'normal'}
              useSubmitBehavior={false}
              onClick={handleCloseButton}
            />
          </ButtonItem>
          <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
            <ButtonOptions
              width={'100%'}
              disabled={loading}
              text={translate!('Save', transFormName)}
              type={'success'}
              useSubmitBehavior
            />
          </ButtonItem>
        </GroupItem>
        {prompt}
      </Form>
    </form>
  );
};
